import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Accordion, AccordionItem, Card, CardBody, Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  getKeyValue,
} from "@heroui/react";
import { faChartColumn, faTable } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ReactApexChart from "react-apexcharts";


import { useFetchWithBody, usePost } from "../../api/reactQueryCustomHooks.jsx";
import { apiRoute, GetLoginCredentialDb } from "../../constants/enum.js";
import LoadingCard from "../../components/LoadingCard.jsx";
import { getMonthNamesBetween } from "../../Utility/utility.js";

// Map month numbers to short names
const monthNumberToName = {
  1: "Jan", 2: "Feb", 3: "Mar", 4: "Apr", 5: "May", 6: "Jun",
  7: "Jul", 8: "Aug", 9: "Sep", 10: "Oct", 11: "Nov", 12: "Dec",
};

const DashboardChart = ({ propDate }) => {
  const { compCode } = useSelector((state) => state.company);
  const [sale, setSale] = useState([]);
  const [purchase, setPurchase] = useState([]);
  const [chartXAxis, setChartXAxis] = useState([]);

  const { mutate: fetchSaleData } = usePost({
    url: apiRoute.dashboard.getTradingData,
    queryKey: "SaleChart",
    onSuccessTasks: (data) => setSale(data || []),
  });

  const { mutate: fetchPurchaseData } = usePost({
    url: apiRoute.dashboard.getTradingData,
    queryKey: "PurchaseChart",
    onSuccessTasks: (data) => setPurchase(data || []),
  });

  useEffect(() => {
    const start = new Date(propDate.start);
    const end = new Date(propDate.end);
    const monthNumbers = [];
    const current = new Date(start);

    while (current <= end) {
      const monthNum = current.getMonth() + 1;
      if (!monthNumbers.includes(monthNum)) monthNumbers.push(monthNum);
      current.setMonth(current.getMonth() + 1);
    }

    setChartXAxis(monthNumbers.map((m) => monthNumberToName[m]));

    fetchSaleData({
      data: {
        compcode: compCode.join(","),
        fromdate: propDate.start,
        todate: propDate.end,
        action: "Sale",
      },
    });

    fetchPurchaseData({
      data: {
        compcode: compCode.join(","),
        fromdate: propDate.start,
        todate: propDate.end,
        action: "Purchase",
      },
    });
  }, [propDate, compCode]);

  const saleChart = useMemo(() => {
    return chartXAxis.map((monthName) => {
      const monthNumber = Number(
        Object.keys(monthNumberToName).find((k) => monthNumberToName[k] === monthName)
      );
      const item = sale.find((s) => Number(s.Month) === monthNumber);
      return item ? Number(item.Total_Value) || 0 : 0;
    });
  }, [sale, chartXAxis]);

  const purchaseChart = useMemo(() => {
    return chartXAxis.map((monthName) => {
      const monthNumber = Number(
        Object.keys(monthNumberToName).find((k) => monthNumberToName[k] === monthName)
      );
      const item = purchase.find((p) => Number(p.Month) === monthNumber);
      return item ? Number(item.Total_Value) || 0 : 0;
    });
  }, [purchase, chartXAxis]);

  const apexChartOptions = {
    chart: { type: "bar", stacked: false, width: "100%" },
    plotOptions: { bar: { horizontal: false, columnWidth: "60%" } },
    xaxis: { categories: chartXAxis },
    yaxis: { labels: { formatter: (val) => val.toFixed(0) } },
    colors: ["#F9C80E", "#5653FE"],
    dataLabels: { enabled: false }, // <-- Disabled labels on top of bars
  };

  const apexChartSeries = [
    { name: "Purchase", data: purchaseChart },
    { name: "Sale", data: saleChart },
  ];

  return (
    <Accordion variant="splitted" defaultExpandedKeys={["1"]}>
      <AccordionItem
        key="1"
        title="Sales & Purchase"
        startContent={<FontAwesomeIcon icon={faChartColumn} />}
      >
        <div className="h-[70vh]">
          <ReactApexChart
            options={apexChartOptions}
            series={apexChartSeries}
            type="bar"
            height="100%"
            width="100%"
          />
        </div>
      </AccordionItem>
    </Accordion>
  );
};

export default DashboardChart;